import { Button, ModalHeader, ModalActions, colors, Select, InputFormContainer, Stack } from '@ui-components/neo.tax';
import { ReactComponent as Calendar } from '@ui-components/neo.tax/dist/icons/calendar.svg';
import React, { useMemo, useState } from 'react';

import { ProductType } from '@neo.tax/shared/graphql';
import { useCurrentCompanyId } from '@neo.tax/shared/hooks';
import { useFlags } from '@neo.tax/shared/third-parties';
import { getProductDefaultPath, PRODUCT_OPTIONS } from '@neo.tax/shared/utils';

import { useNavigateTo } from '../navigate';
import { AvailableProducts, useProductSelectionAnalyticIds } from './hooks';

interface ProductSelectionProps {
  setIsOpen: (isOpen: boolean) => void;
  availableProducts: AvailableProducts;
  defaultProduct: ProductType | null;
}

export const ProductSelection = ({
  availableProducts,
  defaultProduct,
  setIsOpen,
}: ProductSelectionProps): React.ReactElement | null => {
  const companyId = useCurrentCompanyId();
  const navigate = useNavigateTo();
  const { enableSredProduct } = useFlags();
  const { productSelectionModalCancelBtnId, productSelectionModalProductSelect, productSelectionModalSelectBtnId } =
    useProductSelectionAnalyticIds();

  const productOptions = useMemo(
    () =>
      PRODUCT_OPTIONS.filter(({ value }) => {
        // Filter out SR&ED if the feature flag is disabled
        // TEMPORARY CODE: remove once SR&ED is generally available
        if (value === ProductType.CaSred && !enableSredProduct) {
          return false;
        }
        return availableProducts[value].length > 0;
      }),
    [availableProducts, enableSredProduct],
  );

  const [selectedProduct, setSelectedProduct] = useState<ProductType | null>(defaultProduct);

  const handleSubmit = (): void => {
    if (!selectedProduct) {
      return;
    }
    const path = getProductDefaultPath({ product: selectedProduct, companyId });
    navigate(path);
  };

  const handleCancel = (): void => {
    setSelectedProduct(null);
    setIsOpen(false);
  };

  const disabled = !selectedProduct;

  return (
    <Stack direction="column">
      <ModalHeader
        icon={<Calendar width={24} color={colors.blue[600]} />}
        iconBgColor={colors.blue[50]}
        title="Select Product"
        copy="Choose which product to analyze."
      />
      <InputFormContainer inputId={productSelectionModalProductSelect} label="Product" horizontal>
        <Select
          id={productSelectionModalProductSelect}
          autoFocus
          placeholder="Select Product"
          required
          value={selectedProduct ?? undefined}
          onChange={(value): void => {
            setSelectedProduct(value as ProductType);
          }}
          options={productOptions}
          usePortal
        />
      </InputFormContainer>
      <ModalActions hasTopBorder={false}>
        <Button
          variant="white"
          rounded="regular"
          size="base"
          disabled
          onClick={handleCancel}
          id={productSelectionModalCancelBtnId}
        >
          Cancel
        </Button>
        <Button
          disabled={disabled}
          variant="primary"
          rounded="regular"
          size="base"
          onClick={handleSubmit}
          id={productSelectionModalSelectBtnId}
        >
          Select
        </Button>
      </ModalActions>
    </Stack>
  );
};
